/**
 * Incident Editor Dialog - Consolidated Data Tab (PROJ-007)
 * Accordion-based layout combining all static incident data sections
 *
 * PERFORMANCE OPTIMIZED: Lazy loading of accordion content
 * - Only renders content when accordion is expanded
 * - Preserves mounted state to avoid re-fetching data
 * - Reduces initial render time significantly
 *
 * REFACTORED UI/UX (Dec 2025):
 * Reorganized from 9 accordions to 5 logical groups for better UX:
 * 
 * 1. 🔍 Información Básica (Identificación + Clasificación)
 * 2. 👤 Cliente y Partes (Cliente + Partes Involucradas)
 * 3. 👥 Asignación (Responsables y equipos)
 * 4. 📅 Fechas y Resolución (Timeline + Resolution)
 * 5. 🔗 Avanzado (Relaciones + Privacidad/Compliance)
 */

import { useState, useCallback, memo, type ReactNode } from 'react';
import {
  FileText,
  Users,
  UserCheck,
  CalendarClock,
  Settings2,
  ChevronDownIcon
} from 'lucide-react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { Accordion, AccordionContent, AccordionItem } from '@/components/ui/accordion';
import { Separator } from '@/components/ui/separator';
import type { UseFormReturn } from 'react-hook-form';
import type { IncidentFormData } from './types';
import { IdentificationTab } from './IdentificationTab';
import { ClassificationTab } from './ClassificationTab';
import { CustomerSection } from './CustomerSection';
import { PartiesSection } from './PartiesSection';
import { AssignmentSection } from './AssignmentSection';
import { DatesAndSlaSection } from './DatesAndSlaSection';
import { ResolutionAndClosureTab } from './ResolutionAndClosureTab';
import { RelationshipsTab } from './RelationshipsTab';
import { PrivacyTab } from './PrivacyTab';

interface IncidentDataTabProps {
  form: UseFormReturn<IncidentFormData>;
  isEditMode: boolean;
  incidentId?: string;
  isViewMode?: boolean;
  auditInfo?: {
    createdAt?: string | null;
    updatedAt?: string | null;
    createdByName?: string | null;
    updatedByName?: string | null;
  };
}

interface AccordionSectionConfig {
  value: string;
  icon: ReactNode;
  title: string;
  subtitle: string;
}

// REFACTORED: 5 logical groups instead of 9 separate sections
const ACCORDION_SECTIONS: AccordionSectionConfig[] = [
  { 
    value: 'basic_info', 
    icon: <FileText size={16} className="opacity-60" />, 
    title: 'Información Básica', 
    subtitle: 'Título, descripción, categoría y prioridad' 
  },
  { 
    value: 'customer_parties', 
    icon: <Users size={16} className="opacity-60" />, 
    title: 'Cliente y Partes', 
    subtitle: 'Cliente afectado y otras partes involucradas' 
  },
  { 
    value: 'assignment', 
    icon: <UserCheck size={16} className="opacity-60" />, 
    title: 'Asignación', 
    subtitle: 'Responsables, equipos y unidades' 
  },
  { 
    value: 'dates_resolution', 
    icon: <CalendarClock size={16} className="opacity-60" />, 
    title: 'Fechas y Resolución', 
    subtitle: 'Timeline, SLA y resolución del incidente' 
  },
  { 
    value: 'advanced', 
    icon: <Settings2 size={16} className="opacity-60" />, 
    title: 'Avanzado', 
    subtitle: 'Relaciones, privacidad y compliance' 
  },
];

// Default expanded - show most important sections first
const DEFAULT_EXPANDED = ['basic_info'];

const AccordionHeader = memo(function AccordionHeader({
  icon,
  title,
  subtitle
}: {
  icon: ReactNode;
  title: string;
  subtitle: string
}) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between rounded-md py-2 text-left text-[15px] leading-6 font-semibold transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [&[data-state=open]>svg]:rotate-180">
        <span className="flex items-center gap-3">
          <span
            className="flex size-10 shrink-0 items-center justify-center rounded-full border"
            aria-hidden="true"
          >
            {icon}
          </span>
          <span className="flex flex-col space-y-1">
            <span>{title}</span>
            <span className="text-sm font-normal">{subtitle}</span>
          </span>
        </span>
        <ChevronDownIcon
          size={16}
          className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200"
          aria-hidden="true"
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
});

export function IncidentDataTab({ form, isEditMode, incidentId, isViewMode = false, auditInfo }: IncidentDataTabProps) {
  // Track which sections have been mounted (for lazy loading with state preservation)
  const [mountedSections, setMountedSections] = useState<Set<string>>(
    () => new Set(DEFAULT_EXPANDED)
  );

  // Handle accordion value changes to track mounted sections
  const handleValueChange = useCallback((values: string[]) => {
    setMountedSections(prev => {
      const newSet = new Set(prev);
      values.forEach(v => newSet.add(v));
      return newSet;
    });
  }, []);

  // Render section content only if it has been mounted
  // REFACTORED: Combined sections for better UX
  const renderSectionContent = useCallback((sectionValue: string) => {
    if (!mountedSections.has(sectionValue)) {
      return null;
    }

    switch (sectionValue) {
      // COMBINED: Identification + Classification
      case 'basic_info':
        return (
          <div className="space-y-6">
            <IdentificationTab form={form} isEditMode={isEditMode} isViewMode={isViewMode} />
            <Separator className="my-4" />
            <ClassificationTab form={form} isViewMode={isViewMode} />
          </div>
        );
      
      // COMBINED: Customer + Parties
      case 'customer_parties':
        return (
          <div className="space-y-6">
            <CustomerSection form={form} isViewMode={isViewMode} />
            <Separator className="my-4" />
            <PartiesSection incidentId={incidentId || null} isViewMode={isViewMode} />
          </div>
        );
      
      // Assignment (single section)
      case 'assignment':
        return <AssignmentSection form={form} isViewMode={isViewMode} />;
      
      // COMBINED: Dates/SLA + Resolution
      case 'dates_resolution':
        return (
          <div className="space-y-6">
            <DatesAndSlaSection form={form} isEditMode={isEditMode} isViewMode={isViewMode} />
            <Separator className="my-4" />
            <ResolutionAndClosureTab form={form} isViewMode={isViewMode} />
          </div>
        );
      
      // COMBINED: Relationships + Privacy
      case 'advanced':
        return (
          <div className="space-y-6">
            <RelationshipsTab form={form} isViewMode={isViewMode} />
            <Separator className="my-4" />
            <PrivacyTab form={form} isViewMode={isViewMode} />
          </div>
        );
      
      default:
        return null;
    }
  }, [form, isEditMode, isViewMode, incidentId, mountedSections]);

  return (
    <div className="px-6 py-4">
      <Accordion
        type="multiple"
        className="w-full"
        defaultValue={DEFAULT_EXPANDED}
        onValueChange={handleValueChange}
      >
        {ACCORDION_SECTIONS.map((section) => (
          <AccordionItem key={section.value} value={section.value} className="py-2">
            <AccordionHeader
              icon={section.icon}
              title={section.title}
              subtitle={section.subtitle}
            />
            <AccordionContent className="ms-3 ps-10 pb-2">
              {renderSectionContent(section.value)}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
